import { useSettlementsViewModel } from "@/hooks/matter-view-models/use-settlements-view-model";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getConnectionContactName } from "@/lib/utils/connections/item-contact-connections";
import { getLienContactConnection } from "@/lib/utils/connections/lien-contact-connections";
import { getMedicalTreatmentContactConnection } from "@/lib/utils/connections/medical-treatment-contact-connections";
import { calculateExpenseTotals } from "@/lib/utils/matters/expense-utils";
import { calculateLienTotals } from "@/lib/utils/matters/lien-utils";
import { calculateMedicalTreatmentBillTotals } from "@/lib/utils/matters/medical-treatment-bill-utils";
import { calculateNetToClient } from "@/lib/utils/matters/settlement-utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { TopStat } from "../components/TopStat";
import {
  formatCurrency,
  formatMatterStatusDurationDays,
  getActiveMatterStatusPhase,
  getLatestByDate,
  getMatterStatusDayDurations,
  getStatusBarDisplayWidthPercents,
  getTopStats,
  type MatterStatusDayDuration,
} from "../matter-overview-utils";

const STATUS_BAR_DEFAULT_TONE = "bg-blue-500";
const STATUS_BAR_LONGEST_TONE = "bg-amber-500";
const SETTLEMENT_SUMMARY_STAT_PROPS = {
  className:
    "flex min-h-[96px] flex-col justify-center gap-2 px-5 py-6 md:py-7",
  valueClassName: "text-xl font-bold md:text-2xl",
} as const;

type ClosedCardProps = { matter: Matter; title?: string; embedded?: boolean };

export function SettlementSummary({
  matter,
  title = "Settlement & Disbursement Summary",
  embedded = false,
}: ClosedCardProps) {
  const { totalAcceptedSettlements: settlementAmount, netToFirm } =
    useSettlementsViewModel(matter.id);
  const { totalLiens } = calculateLienTotals(matter.liens);
  const { totalExpenses } = calculateExpenseTotals(matter.expenses);
  const { totalBill } = calculateMedicalTreatmentBillTotals(
    matter.medicalTreatmentBills,
  );
  const totalCosts = matter.expenses.reduce(
    (sum, expense) => sum + Number(expense.cost ?? 0),
    0,
  );
  const clientNet = calculateNetToClient(
    settlementAmount,
    totalLiens + totalExpenses + totalBill,
    netToFirm,
  );
  return (
    <div
      className={cn(
        "bg-card min-w-0 rounded-2xl border p-5",
        embedded && "rounded-none border-0 p-0",
      )}
    >
      <h2
        className={cn(
          "text-foreground text-sm font-semibold break-words",
          embedded && "border-b pb-4",
        )}
      >
        {title}
      </h2>

      <div className="mt-5 overflow-hidden rounded-2xl border">
        <div className="grid md:grid-cols-2 xl:grid-cols-4">
          <TopStat
            label="Final settlement"
            value={formatCurrency(settlementAmount)}
            {...SETTLEMENT_SUMMARY_STAT_PROPS}
          />
          <TopStat
            label="Total fees"
            value={<CurrencyWrapper value={netToFirm} as="span" showCents />}
            {...SETTLEMENT_SUMMARY_STAT_PROPS}
          />
          <TopStat
            label="Total costs"
            value={formatCurrency(totalCosts)}
            {...SETTLEMENT_SUMMARY_STAT_PROPS}
          />
          <TopStat
            label="Client net"
            value={formatCurrency(clientNet)}
            {...SETTLEMENT_SUMMARY_STAT_PROPS}
          />
        </div>
      </div>
    </div>
  );
}

export function CycleTimeSummary({
  matter,
  title = "Cycle time summary",
  embedded = false,
}: ClosedCardProps) {
  const { data: matterSettings } = useSuspenseQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const disbursementDate = getLatestByDate(
    matter.trustChecks,
    (check) => check.dateReceived,
  )?.dateReceived;
  const daysOnDesk = getTopStats(matter).daysOnDesk;
  const hasActiveStatusPhase = Boolean(getActiveMatterStatusPhase(matter));
  const statusBarReferenceDate =
    hasActiveStatusPhase ||
    !disbursementDate ||
    !dayjs(disbursementDate).isValid()
      ? undefined
      : disbursementDate;
  const statusDurations = getMatterStatusDayDurations(matter, {
    referenceDate: statusBarReferenceDate,
    matterStatusCatalog: matterSettings.data.statuses,
  });
  const longestStatusSegment = [...statusDurations]
    .sort((a, b) => b.days - a.days)
    .at(0);
  const longestStatusId = longestStatusSegment?.id;
  const statusSegments = statusDurations.map((segment) => ({
    ...segment,
    tone:
      segment.id === longestStatusId
        ? STATUS_BAR_LONGEST_TONE
        : STATUS_BAR_DEFAULT_TONE,
  }));
  const statusBarWidthPercents = getStatusBarDisplayWidthPercents(
    statusSegments.map((segment) => segment.days),
  );
  return (
    <div
      className={cn(
        "bg-card min-w-0 rounded-2xl border p-5",
        embedded && "rounded-none border-0 p-0",
      )}
    >
      <div
        className={cn(
          "flex flex-wrap items-baseline gap-3",
          embedded && "border-b pb-4",
        )}
      >
        <h3 className="text-foreground text-sm font-semibold">{title}</h3>
        <p className="text-foreground text-[28px] leading-none font-bold">
          {daysOnDesk != null ? `${daysOnDesk} days` : "—"}
        </p>
        <p className="text-muted-foreground text-sm">time on desk</p>
      </div>

      {statusSegments.length > 0 ? (
        <>
          <div className="bg-muted mt-6 overflow-hidden rounded-full">
            <div className="flex h-5 w-full">
              {statusSegments.map((segment, index) => (
                <StatusBarSegment
                  key={segment.id}
                  segment={segment}
                  index={index}
                  widthPercent={statusBarWidthPercents[index] ?? 0}
                />
              ))}
            </div>
          </div>

          <div className="text-muted-foreground mt-3 flex w-full text-[11px]">
            {statusSegments.map((segment, index) => (
              <div
                key={segment.id}
                className="min-w-0 shrink-0 px-0.5 text-center"
                style={{
                  width: `${statusBarWidthPercents[index] ?? 0}%`,
                }}
              >
                <div
                  className="text-foreground truncate font-medium"
                  title={segment.label}
                >
                  {segment.label}
                </div>
                <div>{formatMatterStatusDurationDays(segment.days)}</div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-muted-foreground mt-6 text-sm">
          No status history tracked for this matter.
        </p>
      )}

      {longestStatusSegment ? (
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {`Longest in status: ${longestStatusSegment.label} (${formatMatterStatusDurationDays(longestStatusSegment.days)})`}
        </div>
      ) : null}
    </div>
  );
}

export function FinalLienResolutions({
  matter,
  title = "Final lien resolutions",
  embedded = false,
}: ClosedCardProps) {
  const lienResolutionRows = buildFinalLienResolutionRows(matter);
  const totalOriginalAmount = lienResolutionRows.reduce(
    (sum, row) => sum + Number(row.originalAmount ?? 0),
    0,
  );
  const totalFinalAmount = lienResolutionRows.reduce(
    (sum, row) => sum + Number(getDisplayedFinalAmount(row) ?? 0),
    0,
  );
  return (
    <div
      className={cn(
        "bg-card min-w-0 rounded-2xl border p-5",
        embedded && "rounded-none border-0 p-0",
      )}
    >
      <h3
        className={cn(
          "text-foreground text-sm font-semibold break-words",
          embedded && "border-b pb-4",
        )}
      >
        {title}
      </h3>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="text-muted-foreground border-b text-left text-[10px] font-semibold tracking-[0.18em] uppercase">
              <th className="px-0 py-3">Type</th>
              <th className="px-0 py-3">Provider / payer</th>
              <th className="px-0 py-3">Original amount</th>
              <th className="px-0 py-3">Final amount</th>
            </tr>
          </thead>
          <tbody>
            {lienResolutionRows.length > 0 ? (
              lienResolutionRows.map((row) => (
                <tr key={row.id} className="border-b last:border-b-0">
                  <td className="text-muted-foreground px-0 py-3">
                    {row.type}
                  </td>
                  <td className="text-foreground px-0 py-3 font-medium">
                    {row.providerOrPayer}
                  </td>
                  <td className="text-foreground px-0 py-3">
                    {formatCurrency(row.originalAmount)}
                  </td>
                  <td className="text-foreground px-0 py-3 font-semibold">
                    {formatCurrency(getDisplayedFinalAmount(row))}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-muted-foreground px-0 py-6">
                  No liens or bills tracked.
                </td>
              </tr>
            )}
            <tr className="border-t">
              <td className="text-muted-foreground px-0 py-4 font-semibold">
                Total
              </td>
              <td />
              <td className="text-foreground px-0 py-4 font-semibold">
                {formatCurrency(totalOriginalAmount)}
              </td>
              <td className="text-foreground px-0 py-4 font-semibold">
                {formatCurrency(totalFinalAmount)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

type FinalLienResolutionRow = {
  id: string;
  type: string;
  providerOrPayer: string;
  originalAmount: number | null | undefined;
  finalAmount: number | null | undefined;
};

function getDisplayedFinalAmount(row: FinalLienResolutionRow) {
  return row.finalAmount ?? row.originalAmount;
}

function getLienHolderName(
  matter: Matter,
  lienId: string,
  fallbackLabel?: string | null,
) {
  const lien = matter.liens.find((item) => item.id === lienId);
  const connection = getLienContactConnection(lien, "Lien Holder");
  return getConnectionContactName(connection?.contact) || fallbackLabel || "—";
}

function getMedicalProviderName(matter: Matter, treatmentId?: string | null) {
  if (!treatmentId) {
    return "—";
  }

  const treatment = matter.medicalTreatments.find(
    (item) => item.id === treatmentId,
  );
  const connection = getMedicalTreatmentContactConnection(
    treatment,
    "Provider",
  );
  return getConnectionContactName(connection?.contact) ?? "—";
}

function buildFinalLienResolutionRows(
  matter: Matter,
): FinalLienResolutionRow[] {
  const lienRows: FinalLienResolutionRow[] = matter.liens.map((lien) => ({
    id: `lien-${lien.id}`,
    type: lien.category || "—",
    providerOrPayer: getLienHolderName(matter, lien.id, lien.label),
    originalAmount: lien.originalAmount,
    finalAmount: lien.paidAmount,
  }));

  const billRows: FinalLienResolutionRow[] = matter.medicalTreatmentBills
    .filter((bill) => bill.recordStatus === "Received")
    .map((bill) => ({
      id: `bill-${bill.id}`,
      type: "Medical Bill",
      providerOrPayer: getMedicalProviderName(matter, bill.medicalTreatmentId),
      originalAmount: bill.originalBill,
      finalAmount: bill.adjustedBill,
    }));

  return [...lienRows, ...billRows];
}

function StatusBarSegment({
  segment,
  index,
  widthPercent,
}: {
  segment: MatterStatusDayDuration & { tone: string };
  index: number;
  widthPercent: number;
}) {
  return (
    <div
      className={cn(
        "h-full min-w-0",
        segment.tone,
        index > 0 && "border-background border-l",
      )}
      style={{
        width: `${widthPercent}%`,
      }}
      title={`${segment.label} (${formatMatterStatusDurationDays(segment.days)})`}
      aria-current={segment.isCurrent ? "true" : undefined}
    />
  );
}
